<template>
  <portal to="modals">
    <transition name="v-modal" appear>
      <div
        v-show="open"
        :class="['v-modal', mobileTakeover && 'v-modal--mobile-takeover']"
      >
        <transition name="v-modal__frame" appear>
          <div
            class="v-modal__frame"
            :class="[
              'v-modal__frame',
              mobileTakeover && 'v-modal__frame--mobile-takeover',
              logo && mobileTakeover && 'v-modal__frame--has-logo'
            ]"
            role="dialog"
            aria-modal="true"
            :aria-label="
              $attrs['aria-label'] || $t('Dialog', 'components.modal.label')
            "
          >
            <v-icon
              v-if="logo && mobileTakeover"
              name="logo"
              size="small"
              class="v-modal__logo v-modal__logo--mobile-takeover"
              aria-hidden="true"
            />
            <div
              v-if="hasCloseListener"
              @click="$emit('close')"
              @keydown.enter.prevent="$emit('close')"
              @keydown.space.prevent="$emit('close')"
              :class="[
                'v-modal__close',
                mobileTakeover && 'v-modal__close--mobile-takeover'
              ]"
              role="button"
              tabindex="0"
              :aria-label="$t('Luk dialog', 'components.modal.close')"
            >
              <svg
                width="10"
                height="10"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
                focusable="false"
              >
                <g
                  stroke="#5C5C5E"
                  stroke-width="1.5"
                  fill="none"
                  fill-rule="evenodd"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <path d="M1 9l8-8M9 9L1 1" />
                </g>
              </svg>
            </div>
            <div
              :class="[
                'v-modal__content',
                mobileTakeover && 'v-modal__content--mobile-takeover',
                logo && mobileTakeover && 'v-modal__content--has-logo'
              ]"
              tabindex="0"
            >
              <slot></slot>
            </div>
          </div>
        </transition>
      </div>
    </transition>
  </portal>
</template>

<script>
export default {
  props: {
    open: {
      type: Boolean,
      default: false
    },
    mobileTakeover: {
      type: Boolean,
      default: true
    },
    logo: {
      type: Boolean,
      default: false
    }
  },
  mounted() {
    document.addEventListener("keyup", this.closeOnEscape);
  },
  computed: {
    hasCloseListener() {
      return this.$listeners && this.$listeners.close;
    }
  },
  methods: {
    closeOnEscape(event) {
      if (event.keyCode === 27 && this.open) {
        this.$emit("close");
      }
    }
  },
  watch: {
    open(newValue) {
      document.body.style.overflow = newValue ? "hidden" : "";
      if (newValue) {
        this.$nextTick(() => {
          const content =
            this.$el && this.$el.querySelector(".v-modal__content");
          if (content) content.focus();
        });
      }
    }
  },
  beforeDestroy() {
    document.removeEventListener("keyup", this.closeOnEscape);
  }
};
</script>

<style scoped lang="scss">
@import "../../sass/component.scss";

.v-modal {
  position: fixed;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  z-index: 1500;
  background-color: rgba(40, 40, 40, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: opacity 250ms ease-out;
  overflow-y: auto;

  &-enter,
  &-leave-to {
    opacity: 0;
  }

  &--mobile-takeover {
    @include media-breakpoint-down(sm) {
      background-color: transparent;
      overflow-y: hidden;
    }
  }

  &__frame {
    position: relative;
    width: calc(100% - 40px);
    max-height: 100%;
    transition: opacity 250ms ease-out, transform 250ms ease-out;
    transition-delay: 250ms;

    &:before,
    &:after {
      content: "";
      display: block;
      height: 20px;
    }

    &-enter,
    &-leave-to {
      transform: translateY(20px);
      opacity: 0;
    }

    @include media-breakpoint-up(md) {
      height: auto;
      width: 600px;
    }

    &--mobile-takeover {
      @include media-breakpoint-down(sm) {
        width: 100%;
        height: 100%;
        background-color: #fff;
        &:before,
        &:after {
          content: none;
          display: none;
        }
      }
    }
    &--has-logo {
      @include media-breakpoint-down(sm) {
        padding-block-start: 4.5rem;
      }
    }
  }

  &__content {
    height: auto;
    width: 100%;
    overflow: visible;
    padding: $spacer;
    padding-block-end: $spacer * 3; // iOS Safari address-bar viewport overlap fix
    background-color: #fff;
    margin-bottom: $spacer;
    border-radius: $border-radius;
    @include media-breakpoint-up(md) {
      padding: $spacer * 2;
    }

    &--mobile-takeover {
      @include media-breakpoint-down(sm) {
        width: 100%;
        height: 100%;
        overflow-y: auto;
        margin-bottom: 0;
        border-radius: 0;
      }
    }
    &--has-logo {
      @include media-breakpoint-down(sm) {
        padding-block-start: 0 !important;
      }
    }
  }

  &__close {
    position: absolute;
    inset-block-start: 30px;
    inset-inline-end: 10px;
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1;
    width: 26px;
    height: 26px;
    background-color: theme-color("gray");
    cursor: pointer;
    transition: background-color 200ms ease-out;
    border-radius: 50%;

    &:hover {
      background-color: theme-color("gray");
      filter: brightness(0.9);
    }

    &--mobile-takeover {
      @include media-breakpoint-down(sm) {
        inset-block-start: 10px;
        inset-inline-end: 10px;
      }
    }
  }

  &__logo {
    &--mobile-takeover {
      display: none;
      @include media-breakpoint-down(sm) {
        display: block;
        position: absolute;
        inset-block-start: 17px;
        inset-inline-start: $spacer;
        color: theme-color("primary");
      }
    }
  }

  // Print styles for modal:
  @media print {
    position: absolute !important;
    height: auto !important;
    overflow: visible !important;
    background-color: transparent !important;

    &__frame {
      max-height: none !important;
    }

    &__content {
      overflow: visible !important;
      height: auto !important;
    }

    &__close {
      display: none !important;
    }

    // Add specific print adjustments for cards inside a modal:
    :deep {
      .v-card {
        page-break-after: auto !important;
        page-break-before: auto !important;
        page-break-inside: auto !important;
        margin-bottom: 0.5cm !important;
        background: none !important;
        .card-inner-content {
          padding: 0 !important;
        }
        p,
        .v-text {
          page-break-inside: auto !important;
          margin-bottom: 0.5cm !important;
        }
      }
      .v-input {
        page-break-after: auto !important;
        page-break-before: auto !important;
        page-break-inside: avoid !important;
        margin-top: 0.75cm !important;
      }
    }
  }
}
</style>
